import { create } from "zustand";
import { persist } from "zustand/middleware";
import { queryClient } from "@/lib/api/queryClient";

export type AuthProvider = "local" | "google" | "microsoft" | "apple";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  role: "agent" | "agent_owner_org";
  brokerId: string;
  organizationId?: string;
  authProvider: AuthProvider;
  /** false = corretor ainda não completou os dados de onboarding (vem do backend) */
  profileCompleted?: boolean;
  /** false = corretor ainda não aceitou os termos legais vigentes (gate de acesso, vem do backend) */
  termsAccepted?: boolean;
  /** false = e-mail ainda não confirmado (soft gate — mostra CTA, não bloqueia; vem do backend, hub#345) */
  emailVerified?: boolean;
}

interface AuthState {
  // Access token is in-memory only — never persisted (OWASP A02)
  accessToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  setSession: (params: { accessToken: string; user: AuthUser }) => void;
  /** Atualiza campos do usuário em sessão (ex.: marcar perfil/termos concluídos no onboarding). */
  patchUser: (partial: Partial<AuthUser>) => void;
  refresh: () => Promise<string | null>;
  logout: () => Promise<void>;
}

// Renovação única em voo (single-flight): o refresh token (cookie HttpOnly) é
// ROTACIONADO no primeiro uso — dois POST /auth/refresh concorrentes derrubam a
// sessão. Compartilhada por TODOS os chamadores (interceptor axios e cliente SSE).
let refreshInFlight: Promise<string | null> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      user: null,
      isAuthenticated: false,

      setSession: ({ accessToken, user }) => {
        // Limpa o cache de queries ao iniciar nova sessão — impede que dados em
        // cache de outro corretor (mesmo navegador) vazem para o novo login.
        queryClient.clear();
        set({ accessToken, user, isAuthenticated: true });
      },

      patchUser: (partial) =>
        set((state) => (state.user ? { user: { ...state.user, ...partial } } : {})),

      refresh: async () => {
        refreshInFlight ??= (async () => {
          try {
            const { getAuthClient } = await import("../api");
            // No body — relies on HttpOnly rt cookie sent automatically
            const result = await getAuthClient().refresh();
            set({ accessToken: result.accessToken, user: result.user });
            return result.accessToken;
          } catch {
            set({ accessToken: null, user: null, isAuthenticated: false });
            return null;
          } finally {
            refreshInFlight = null;
          }
        })();
        return refreshInFlight;
      },

      logout: async () => {
        try {
          const { getAuthClient } = await import("../api");
          await getAuthClient().logout();
        } catch {
          // Server-side revocation failed — clear client state regardless
        } finally {
          set({ accessToken: null, user: null, isAuthenticated: false });
          // Descarta dados em cache da sessão encerrada (privacidade entre corretores).
          queryClient.clear();
        }
      },
    }),
    {
      name: "imobgo.auth",
      // Only persist non-sensitive metadata — tokens are never stored in localStorage
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
